/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import {
  Avatar,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import ButtonCircle from '../ButtonCircle';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { IObject, TObjectFilter } from 'src/models/objectManagement';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { useLocation } from 'react-router';
import SectionSocialProfile from './components/SectionSocialProfile';
import SectionKeywordHashtag from './components/SectionKeywordHashtag';
import SectionHashtag from './components/SectionHashtag';
import CloseIcon from '@mui/icons-material/Close';
import TooltipCustomize from '../TooltipCustomize';
import useCustomDashboard from 'src/services/useCustomDashboard';
import { reducerUpdateDashboardList } from 'src/features/customDashboard';
import {
  reducerUpdateGroupObject,
  reducerUpdateObjectListByObjectType,
  reducerUpdateSelectedObjectType
} from 'src/features/objectManagement';
import { SETTINGS_GROUP } from 'src/route';
import useObjectManagement from 'src/services/useObjectManagement';
import { useAlert } from 'src/hooks/useAlert';
import { objectTypeOptions } from 'src/content/settings/MonitoredAssets/components/HeaderObjects';

interface Props {
  title?: string;
  isCampaign?: boolean;
  isHidden?: boolean;
  onCloseEdit?: () => void;
  isEditing?: boolean;
  isDashboard?: boolean;
}

const ModalManageObjectByGroup: React.FC<Props> = ({
  title,
  isCampaign = false,
  isHidden = false,
  isEditing = false,
  onCloseEdit,
  isDashboard = false
}) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const pathSocialMedia = pathname.split('/')[2];

  const [open, setOpen] = useState(false);
  const [hide, setHide] = useState(false);
  const { objectManagementEditDashboard } = useCustomDashboard();
  const { objectManagementEditGroup } = useObjectManagement();
  const selectedObjectType = useAppSelector(
    (state) => state.objectManagement.selectedObjectType
  );
  const { handleClickAlert } = useAlert();
  const autoRefreshInterval = useAppSelector(
    (state) => state.autoRefreshMetric.selectedAutoRefreshInterval
  );
  // const [selectedObjectType, setSelectedObjectType] =
  //   useState<TObjectFilter>('all');

  const handleClickOpen = () => {
    if (autoRefreshInterval > 0) {
      handleClickAlert({
        vertical: 'top',
        horizontal: 'center',
        severity: 'warning',
        message: 'You can manage objects only when auto refresh is off'
      });
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const projectId = useAppSelector((state) => state.users.project.id);

  const { dashboardList, dashboardSelectedById } = useAppSelector(
    (state) => state.storeCustomDashboard
  );

  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );

  const filteredObjectsByType = useMemo(() => {
    if (objectList.length <= 0) return [];
    if (selectedObjectType === 'entity')
      return objectList.filter((object) => object.isEntity);
    if (selectedObjectType === 'object')
      return objectList.filter((object) => !object.isEntity);
    return objectList;
  }, [objectList, selectedObjectType]);

  const socialProfileObjList = useMemo(() => {
    return filteredObjectsByType.filter((obj) => obj.streamType === 'account');
  }, [filteredObjectsByType]);

  const keywordObjList = useMemo(() => {
    return filteredObjectsByType.filter((obj) => obj.streamType === 'keyword');
  }, [filteredObjectsByType]);

  const hashtagObjList = useMemo(() => {
    if (!isCampaign) return [];
    return keywordObjList.filter((obj) => obj.isHashtag);
  }, [filteredObjectsByType]);

  const objectIdList = useAppSelector(
    (state) => state.analyticArguments.objectIdList
  );

  const groupObjects = useAppSelector(
    (state) => state.objectManagement.groupObject
  );

  const selectedGroupId = useAppSelector(
    (state) => state.objectManagement.groupSelectedById
  );
  // get group name by selected group id
  const groupName = useMemo(() => {
    const selectedGroup = groupObjects.data.find(
      (group) => group.id === selectedGroupId
    );
    return selectedGroup?.displayName ?? '';
  }, [selectedGroupId]);

  const [selectedGroupName, setSelectedGroupName] = useState<string>();

  useEffect(() => {
    setSelectedGroupName(groupName);
  }, [selectedGroupId]);

  const activeObjectIds = useMemo(() => {
    return objectList.filter((object) =>
      objectIdList.find((id) => id === object.id)
    );
  }, [objectIdList]);

  const getActiveSocialProfileIds = () => {
    return activeObjectIds
      .filter((obj) => obj.streamType === 'account')
      .map((obj) => obj.id);
  };

  const getActiveKeywordAndHashtag = () => {
    return activeObjectIds
      .filter((object) => object.streamType === 'keyword')
      .map((object) => object.id);
  };

  const hashtagList = useAppSelector((state) =>
    state.storeSocialMediaCampaign.campaignSelected?.campaignObjectDetails
      ? state.storeSocialMediaCampaign.campaignSelected?.campaignObjectDetails.map(
          (campaign) => campaign.objectModel
        )
      : []
  );

  const [selectedSocialProfileIds, setSelectedSocialProfileIds] = useState<
    Array<string>
  >(getActiveSocialProfileIds);

  useEffect(() => {
    setSelectedSocialProfileIds(getActiveSocialProfileIds());
  }, [activeObjectIds]);

  const [selectedKeywordIds, setSelectedKeywordIds] = useState<Array<string>>(
    getActiveKeywordAndHashtag
  );
  useEffect(() => {
    setSelectedKeywordIds(getActiveKeywordAndHashtag());
  }, [activeObjectIds]);

  const [selectedHashtagIds, setSelectedHashtagIds] = useState<Array<string>>(
    hashtagList.map((object) => object.id)
  );

  // set active hashtag for campaign monitoring
  useEffect(() => {
    if (!isCampaign) return;
    const activeHashtagObjects = activeObjectIds.filter(
      (object) => object.isHashtag === true
    );
    const activeHashtagObjectIds = activeHashtagObjects.map(
      (object) => object.id
    );
    setSelectedHashtagIds(activeHashtagObjectIds);
  }, [isCampaign, activeObjectIds]);

  const isPageHaveAKeyword = () => {
    switch (pathSocialMedia) {
      case 'content':
        return false;
      case 'community':
        return false;
      default:
        return true;
    }
  };

  const handleClickDone = () => {
    const objectListActive = isCampaign
      ? selectedHashtagIds
      : isPageHaveAKeyword()
      ? selectedSocialProfileIds.concat(selectedKeywordIds)
      : selectedSocialProfileIds;

    // edit group
    if (location.pathname === SETTINGS_GROUP) {
      const payload = {
        projectId: projectId,
        objectList: objectListActive,
        objectGroupId: selectedGroupId,
        groupName: selectedGroupName,
        templateName: 'content'
      };
      objectManagementEditGroup({ variables: payload }).then((response) => {
        const editedGroup = response.data.objectManagement_editObjectGroup;
        const updatedGroups = groupObjects.data.map((group) => {
          if (editedGroup.id === group.id) return { ...editedGroup };
          return group;
        });
        dispatch(
          reducerUpdateGroupObject({ ...groupObjects, data: updatedGroups })
        );
        dispatch(reducerUpdateObjectListByObjectType(editedGroup.objectList));
        dispatch(
          reducerUpdateAnalyticArgumentObjectIdList(
            editedGroup.objectList.map((object) => object.id)
          )
        );
      });
      onCloseEdit();
      return;
    }

    // edit custom dashboard
    if (isDashboard && isEditing) {
      const tempDashboard = dashboardList.data.filter(
        (dashboard) => dashboard.id === dashboardSelectedById
      )[0];
      const payload = {
        displayName: tempDashboard.displayName,
        objectIds: objectListActive,
        projectId,
        customDashboardId: dashboardSelectedById
      };
      objectManagementEditDashboard({
        variables: payload
      }).then((response) => {
        const { id, objectList } =
          response.data?.customDashboard_editCustomDashboard;
        const objectIdList = objectList.map((object: IObject) => object.id);
        const updateDashboard = dashboardList.data.map((dashboard) => {
          if (dashboard.id === id) {
            return { ...dashboard, objectList };
          }
          return dashboard;
        });
        dispatch(
          reducerUpdateDashboardList({
            ...dashboardList,
            data: updateDashboard
          })
        );
        dispatch(reducerUpdateObjectListByObjectType(objectList));
        dispatch(reducerUpdateAnalyticArgumentObjectIdList(objectIdList));
        isEditing ? onCloseEdit() : handleClose();
      });
      return;
    }

    // for add new group via group selection drop down
    if (isEditing) {
      const payload = {
        projectId: projectId,
        objectList: objectListActive,
        objectGroupId: selectedGroupId,
        groupName: selectedGroupName,
        templateName: 'content'
      };
      objectManagementEditGroup({ variables: payload }).then((res) => {
        const editedGroup = res.data.objectManagement_editObjectGroup;
        const updatedGroups = groupObjects.data.map((group) => {
          if (editedGroup.id === group.id) return { ...editedGroup };
          return group;
        });
        dispatch(
          reducerUpdateGroupObject({ ...groupObjects, data: updatedGroups })
        );
      });
    }

    // for change active object via modal
    const newObjectList = objectList
      .filter((val) => {
        return objectListActive.includes(val.id);
      })
      .map((val) => {
        return val;
      });

    dispatch(reducerUpdateObjectListByObjectType(newObjectList));
    dispatch(reducerUpdateAnalyticArgumentObjectIdList(objectListActive));
    isEditing ? onCloseEdit() : handleClose();
  };

  const handleChangeObjectType = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(reducerUpdateSelectedObjectType(selectedValue as TObjectFilter));
    // setSelectedObjectType(selectedValue as TObjectFilter);
    const objectListByType = objectList.filter((object) => {
      if (selectedValue === 'entity') return object.isEntity;
      if (selectedValue === 'object') return !object.isEntity;
      return true;
    });

    // reset selected obj to all obj
    setSelectedSocialProfileIds(
      objectListByType
        .filter((object) => object.streamType === 'account')
        .map((object) => object.id)
    );
    setSelectedKeywordIds(
      objectListByType
        .filter((object) => object.streamType === 'keyword')
        .map((object) => object.id)
    );
    if (isCampaign) {
      setSelectedHashtagIds(
        objectListByType
          .filter(
            (object) => object.streamType === 'keyword' && object.isHashtag
          )
          .map((obj) => obj.id)
      );
    }
  };

  const isGroupActive = selectedGroupId !== '_default';

  return (
    <>
      {isHidden ? null : (
        <ButtonCircle onClick={handleClickOpen}>
          <TooltipCustomize title="Manage Object" placement="bottom-start">
            <Avatar
              sx={{
                width: 24,
                height: 24,
                background: '#E0F6FF'
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.79998 18C4.48172 18 4.17649 18.1264 3.95145 18.3514C3.7264 18.5765 3.59998 18.8817 3.59998 19.2C3.59998 19.5182 3.7264 19.8234 3.95145 20.0485C4.17649 20.2735 4.48172 20.4 4.79998 20.4H13.5216C13.7322 20.7648 14.0352 21.0677 14.4 21.2783C14.7649 21.489 15.1787 21.5999 15.6 21.5999C16.0212 21.5999 16.4351 21.489 16.7999 21.2783C17.1648 21.0677 17.4677 20.7648 17.6784 20.4H19.2C19.5182 20.4 19.8235 20.2735 20.0485 20.0485C20.2735 19.8234 20.4 19.5182 20.4 19.2C20.4 18.8817 20.2735 18.5765 20.0485 18.3514C19.8235 18.1264 19.5182 18 19.2 18H17.6784C17.4677 17.6351 17.1648 17.3322 16.7999 17.1216C16.4351 16.9109 16.0212 16.8001 15.6 16.8001C15.1787 16.8001 14.7649 16.9109 14.4 17.1216C14.0352 17.3322 13.7322 17.6351 13.5216 18H4.79998ZM4.79998 10.8C4.48172 10.8 4.17649 10.9264 3.95145 11.1514C3.7264 11.3765 3.59998 11.6817 3.59998 12C3.59998 12.3182 3.7264 12.6234 3.95145 12.8485C4.17649 13.0735 4.48172 13.2 4.79998 13.2H6.32158C6.53223 13.5648 6.83519 13.8677 7.20003 14.0783C7.56486 14.289 7.97871 14.3999 8.39998 14.3999C8.82124 14.3999 9.23509 14.289 9.59992 14.0783C9.96476 13.8677 10.2677 13.5648 10.4784 13.2L19.2 13.2C19.5182 13.2 19.8235 13.0735 20.0485 12.8485C20.2735 12.6234 20.4 12.3182 20.4 12C20.4 11.6817 20.2735 11.3765 20.0485 11.1514C19.8235 10.9264 19.5182 10.8 19.2 10.8L10.4784 10.8C10.2677 10.4351 9.96476 10.1322 9.59992 9.92157C9.23509 9.71095 8.82124 9.60006 8.39998 9.60006C7.97871 9.60006 7.56486 9.71095 7.20003 9.92157C6.83519 10.1322 6.53223 10.4351 6.32158 10.8H4.79998ZM3.59998 4.79996C3.59998 4.4817 3.7264 4.17647 3.95145 3.95143C4.17649 3.72639 4.48172 3.59996 4.79998 3.59996H13.5216C13.7322 3.23514 14.0352 2.9322 14.4 2.72157C14.7649 2.51095 15.1787 2.40006 15.6 2.40006C16.0212 2.40006 16.4351 2.51095 16.7999 2.72157C17.1648 2.9322 17.4677 3.23514 17.6784 3.59996H19.2C19.5182 3.59996 19.8235 3.72639 20.0485 3.95143C20.2735 4.17647 20.4 4.4817 20.4 4.79996C20.4 5.11822 20.2735 5.42344 20.0485 5.64849C19.8235 5.87353 19.5182 5.99996 19.2 5.99996L17.6784 5.99996C17.4677 6.36478 17.1648 6.66772 16.7999 6.87834C16.4351 7.08897 16.0212 7.19985 15.6 7.19985C15.1787 7.19985 14.7649 7.08897 14.4 6.87834C14.0352 6.66772 13.7322 6.36478 13.5216 5.99996H4.79998C4.48172 5.99996 4.17649 5.87353 3.95145 5.64849C3.7264 5.42344 3.59998 5.11822 3.59998 4.79996Z"
                  fill="#469DDD"
                />
              </svg>
            </Avatar>
          </TooltipCustomize>
        </ButtonCircle>
      )}

      <Dialog
        onClose={
          isEditing
            ? () => {
                setSelectedGroupName(groupName);
                onCloseEdit();
              }
            : handleClose
        }
        open={isEditing ? isEditing : open}
        fullWidth
        maxWidth={'md'}
        sx={{ opacity: hide ? 0 : 1 }}
      >
        <IconButton
          aria-label="close"
          onClick={
            isEditing
              ? () => {
                  setSelectedGroupName(groupName);
                  onCloseEdit();
                }
              : handleClose
          }
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ pt: 8, px: 6 }}
        >
          <Typography variant="h3">Manage {title ?? 'Objects'}</Typography>
          {!isEditing ? (
            <Select
              id="objectType"
              name="objectType"
              value={selectedObjectType}
              onChange={handleChangeObjectType}
              sx={{ minWidth: '140px' }}
            >
              <MenuItem key="placeholder" id="placeholder" value="" disabled>
                Object Type
              </MenuItem>
              {objectTypeOptions.map(({ id, value, label }) => (
                <MenuItem
                  key={id}
                  id={id}
                  value={value}
                  disabled={
                    // disable filtering when group is active
                    isGroupActive && (id === 'entity' || id === 'object')
                  }
                >
                  {label}
                </MenuItem>
              ))}
            </Select>
          ) : null}
        </Stack>
        {isCampaign ? (
          <SectionHashtag
            objectList={hashtagObjList}
            objectListIdActive={selectedHashtagIds}
            onParentVisible={() => setHide((prev) => !prev)}
            onSelect={(hashtagIds) => setSelectedHashtagIds(hashtagIds)}
          />
        ) : (
          <>
            {isEditing && (
              <Stack sx={{ mt: theme.spacing(4), mx: theme.spacing(5) }}>
                <DialogContentText sx={{ mb: theme.spacing(0.5) }}>
                  Group name
                </DialogContentText>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={selectedGroupName}
                  onChange={(e) => setSelectedGroupName(e.target.value)}
                />
              </Stack>
            )}
            <SectionSocialProfile
              objectList={socialProfileObjList}
              objectListIdActive={selectedSocialProfileIds}
              onParentVisible={() => setHide((prev) => !prev)}
              onSelect={(socialProfileIds) =>
                setSelectedSocialProfileIds(socialProfileIds)
              }
            />
            {isPageHaveAKeyword() && (
              <SectionKeywordHashtag
                objectList={keywordObjList}
                objectListIdActive={selectedKeywordIds}
                onParentVisible={() => setHide((prev) => !prev)}
                onSelect={(socialProfileIds) =>
                  setSelectedKeywordIds(socialProfileIds)
                }
              />
            )}
          </>
        )}

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pb: theme.spacing(5)
          }}
        >
          <Button
            variant="contained"
            sx={{
              background: theme.palette.primary.dark,
              width: theme.spacing(20),
              color: theme.colors.primary.lighter
            }}
            size="large"
            onClick={handleClickDone}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalManageObjectByGroup;
